import React, { useCallback } from "react";
import { cloneDeep, debounce, get } from "lodash";
import PropTypes from "prop-types";
import { useTheme } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Select, Box, Toggle } from "orcs-design-system";
import {
  useFilters,
  useApplyFilters,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { DEBOUNCE_SEARCH_TIME } from "src/consts/global";
import FilterOptionPropType from "../../../custom-prop-types/filterOption";
import formatGroupLabel from "./formatGroupLabel";
import formatOptionLabel from "./formatOptionLabel";
import { updateFilterValues } from "./filterConfigs";

// Selected values default to the full-size ORCS chip, which crowds the narrow
// filter sidebar. Tag options bring their own chip, so their label is bare.
const compactSelectedValueStyles =
  ({ radius, hasOwnChip }) =>
  (styles) => ({
    ...styles,
    multiValueLabel: (provided, state) => ({
      ...styles.multiValueLabel(provided, state),
      padding: hasOwnChip ? "0 !important" : "2px 4px 2px 6px !important",
      borderRadius: state.data.isFixed ? radius : `${radius} 0 0 ${radius}`,
    }),
    multiValueRemove: (provided, state) => ({
      ...styles.multiValueRemove(provided, state),
      padding: "2px 3px 2px 2px",
      minWidth: "18px",
      minHeight: "18px",
      borderRadius: `0 ${radius} ${radius} 0`,
    }),
  });

const FilterSelect = (props) => {
  const {
    onInputChange,
    isSearching,
    options,
    valueKeys,
    toggles,
    name,
    optionLabelType,
    filterOption,
    backspaceRemovesValue,
    isMulti = true,
    ...selectProps
  } = props;

  const filters = useFilters();
  const theme = useTheme();

  const applyFilters = useApplyFilters();
  const rawSingleValue = get(filters, valueKeys[0], []);
  const filterValue = isMulti
    ? valueKeys.map((key) => get(filters, key, [])).flat()
    : Array.isArray(rawSingleValue)
      ? (rawSingleValue[0] ?? null)
      : (rawSingleValue ?? null);

  const onFilterValueChange = useCallback(
    (values) => {
      const updatedFilters = updateFilterValues(
        filters,
        name,
        valueKeys,
        values
      );
      applyFilters(updatedFilters);
    },
    [filters, name, applyFilters, valueKeys]
  );

  const onSelectGroupLabel = useCallback(
    (key, value) => {
      const currentFilters = cloneDeep(filters);
      currentFilters[key].unshift(value);
      applyFilters(currentFilters);
    },
    [filters, applyFilters]
  );

  const select = (
    <Select
      placeholder="Search or select"
      data-testid={selectProps.inputId}
      {...selectProps}
      options={options}
      value={filterValue}
      filterOption={filterOption}
      isMulti={isMulti}
      onChange={(value) =>
        onFilterValueChange(isMulti ? value : value ? [value] : [])
      }
      onInputChange={
        onInputChange && debounce(onInputChange, DEBOUNCE_SEARCH_TIME)
      }
      backspaceRemovesValue={backspaceRemovesValue}
      isLoading={isSearching}
      updateStyles={compactSelectedValueStyles({
        radius: themeGet("radii.2")({ theme }),
        hasOwnChip: optionLabelType === "tagOption",
      })}
      formatOptionLabel={formatOptionLabel[optionLabelType]}
      formatGroupLabel={(data) => formatGroupLabel(data, onSelectGroupLabel)}
    />
  );

  return (
    <Box mb="r">
      {select}
      {toggles.length > 0 && (
        <Box mt="r">
          {toggles.map((toggle) => (
            <Toggle
              key={toggle.id}
              small
              id={toggle.id}
              label={toggle.label}
              className={`guided-tour-filter-toggle-${toggle.id}`}
              checked={get(filters, toggle.valueKey)}
              onChange={(e) => {
                onFilterValueChange({
                  value: e.target.checked,
                  valueKey: toggle.valueKey,
                });
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

FilterSelect.defaultProps = {
  isSearching: false,
  filterOption: false,
  backspaceRemovesValue: false,
  toggles: [],
};

FilterSelect.propTypes = {
  options: PropTypes.arrayOf(FilterOptionPropType).isRequired,
  onInputChange: PropTypes.func.isRequired,
  isSearching: PropTypes.bool,
  valueKeys: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  optionLabelType: PropTypes.string,
  toggles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      valueKey: PropTypes.string.isRequired,
    })
  ),
  name: PropTypes.string.isRequired,
  filterOption: PropTypes.bool,
  backspaceRemovesValue: PropTypes.bool,
  isMulti: PropTypes.bool,
};

export default FilterSelect;
